// DailyRoundupCard.js
// Renders a single DailyWrapData document from Firestore.
import React from 'react';
import MMIGauge from './MMIGauge';
import GiftNiftySection from './GiftNiftySection';
import SortableSectorList from './SortableSectorList';
import './MarketWrap.css';

// ── Small helpers ────────────────────────────────────────────────────────────

const ArrowUp   = () => <span className="arrow-up">▲</span>;
const ArrowDown = () => <span className="arrow-down">▼</span>;

const ChangeCell = ({ value, direction = 'auto' }) => {
    const v = Number(value ?? 0);
    const isUp = direction === 'up' ? true : direction === 'down' ? false : v >= 0;
    const cls = isUp ? 'chg-up' : 'chg-down';
    return (
        <span className={cls}>
            {isUp ? <ArrowUp /> : <ArrowDown />}
            {Math.abs(v).toFixed(2)}%
        </span>
    );
};

const toneCls = (tone) => {
    const map = { Positive: 'tone-positive', Cautious: 'tone-cautious', Volatile: 'tone-volatile', Mixed: 'tone-mixed' };
    return map[tone] || '';
};

const formatClose = (v) =>
    v ? Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—';

// ── Main component ────────────────────────────────────────────────────────────

const DailyRoundupCard = ({ wrap }) => {

    if (!wrap || !wrap.data) return null;

    const { data, title, createdAt } = wrap;
    const { meta, benchmarks, institutionalFlows, sectoralSnapshot, gainers, losers,
            stocksInNews, globalIndices, currency, commodities, ipoTracker,
            optionsSnapshot, eventsToday, ourView, mmi, giftNifty } = data;

    const dateLabel = createdAt
        ? new Date(createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
        : meta?.date || '';
    const asOfTimeLabel = createdAt
        ? new Date(createdAt).toLocaleTimeString('en-IN', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata',
        })
        : null;

    return (
        <div className="wrap-card">
            {/* Header */}
            <div className="wrap-card-header">
                <div>
                    <h2 className="wrap-card-title">{title || `Daily Market Roundup — ${meta?.date}`}</h2>
                    <p className="wrap-card-date">
                        {dateLabel}
                        {asOfTimeLabel ? ` • As of ${asOfTimeLabel} IST` : ''}
                    </p>
                </div>
                <span className={`wrap-tone-badge ${toneCls(meta?.tone)}`}>
                    {meta?.tone || 'Mixed'}
                </span>
            </div>

            <div className="wrap-card-body">

                {/* MMI Gauge */}
                <MMIGauge mmi={mmi} type="daily" />

                {/* GIFT Nifty */}
                <GiftNiftySection giftNifty={giftNifty} />

                {/* Opening summary */}
                {meta?.openingSummary && (
                    <div className="wrap-opening">
                        <strong>Key Theme:</strong> {meta.keyTheme && <em>{meta.keyTheme} — </em>}
                        {meta.openingSummary}
                    </div>
                )}

                {/* Benchmarks */}
                {benchmarks?.rows?.length > 0 && (
                    <div className="wrap-section">
                        <h3 className="wrap-section-title">Index Snapshot</h3>
                        <table className="benchmark-table">
                            <caption className="visually-hidden">Benchmark index closes and day-on-day change</caption>
                            <thead>
                                <tr>
                                    <th scope="col">Index</th>
                                    <th scope="col">Close</th>
                                    <th scope="col">DoD Chg</th>
                                </tr>
                            </thead>
                            <tbody>
                                {benchmarks.rows.map((row, i) => (
                                    <tr key={i}>
                                        <td>{row.name}</td>
                                        <td>{formatClose(row.close)}</td>
                                        <td><ChangeCell value={row.wowPercent} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {benchmarks.narrative && (
                            <div className="wrap-description">{benchmarks.narrative}</div>
                        )}
                    </div>
                )}

                {/* Gainers & Losers */}
                {(gainers?.length > 0 || losers?.length > 0) && (
                    <div className="wrap-section">
                        <h3 className="wrap-section-title">Top Movers</h3>
                        {gainers?.length > 0 && (
                            <>
                                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.4rem' }}>GAINERS</p>
                                <div className="mover-list" style={{ marginBottom: '0.6rem' }}>
                                    {gainers.map((m, i) => (
                                        <span key={i} className="mover-pill up">
                                            {m.stock} <ChangeCell value={m.wowPercent} direction="up" />
                                        </span>
                                    ))}
                                </div>
                            </>
                        )}
                        {losers?.length > 0 && (
                            <>
                                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.4rem' }}>LOSERS</p>
                                <div className="mover-list">
                                    {losers.map((m, i) => (
                                        <span key={i} className="mover-pill down">
                                            {m.stock} <ChangeCell value={m.wowPercent} direction="down" />
                                        </span>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Sectoral snapshot */}
                {sectoralSnapshot?.rows?.length > 0 && (
                    <div className="wrap-section">
                        <h3 className="wrap-section-title">Sectoral Movers</h3>
                        <SortableSectorList
                            rows={sectoralSnapshot.rows}
                            narrative={sectoralSnapshot.narrative}
                        />
                    </div>
                )}

                {/* Stocks in news — positive group first, then negative */}
                {stocksInNews?.length > 0 && (() => {
                    const positives = stocksInNews.filter(i => i.sentiment === 'positive');
                    const negatives = stocksInNews.filter(i => i.sentiment === 'negative');
                    const renderItem = (item, i) => (
                        <div key={i} className="news-item">
                            <div className={`news-sentiment-dot ${item.sentiment}`} />
                            <span>
                                <span className="news-company">{item.company}:</span>
                                <span className="news-summary"> {item.summary}</span>
                            </span>
                        </div>
                    );
                    return (
                        <div className="wrap-section">
                            <h3 className="wrap-section-title">Stocks in the News</h3>
                            {positives.length > 0 && (
                                <>
                                    <p style={{ fontSize: '0.72rem', color: '#15803d', fontWeight: 700, marginBottom: '0.35rem', letterSpacing: '0.05em' }}>POSITIVE</p>
                                    {positives.map(renderItem)}
                                </>
                            )}
                            {negatives.length > 0 && (
                                <>
                                    <p style={{ fontSize: '0.72rem', color: '#b91c1c', fontWeight: 700, margin: `${positives.length > 0 ? '0.75rem' : '0'} 0 0.35rem`, letterSpacing: '0.05em' }}>NEGATIVE</p>
                                    {negatives.map(renderItem)}
                                </>
                            )}
                        </div>
                    );
                })()}

                {/* Global Indices — day-on-day */}
                {globalIndices?.rows?.length > 0 && (
                    <div className="wrap-section">
                        <h3 className="wrap-section-title">Global Indices</h3>
                        <table className="benchmark-table">
                            <caption className="visually-hidden">Global indices day-on-day change</caption>
                            <thead>
                                <tr><th scope="col">Index</th><th scope="col">DoD Chg</th></tr>
                            </thead>
                            <tbody>
                                {globalIndices.rows.map((row, i) => (
                                    <tr key={i}>
                                        <td>{row.index}</td>
                                        <td><ChangeCell value={row.wowPercent} direction={row.direction} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {globalIndices.narrative && (
                            <div className="wrap-description">{globalIndices.narrative}</div>
                        )}
                    </div>
                )}

                {/* Currency — day-on-day */}
                {currency?.rows?.length > 0 && (
                    <div className="wrap-section">
                        <h3 className="wrap-section-title">Currency</h3>
                        {currency.rows.map((row, i) => (
                            <div key={i} className="sector-row">
                                <span className="sector-name">{row.pair}</span>
                                <ChangeCell value={row.wowPercent} direction={row.direction} />
                            </div>
                        ))}
                        {currency.narrative && (
                            <div className="wrap-description">{currency.narrative}</div>
                        )}
                    </div>
                )}

                {/* Commodities — day-on-day */}
                {commodities?.rows?.length > 0 && (
                    <div className="wrap-section">
                        <h3 className="wrap-section-title">Commodities</h3>
                        <table className="benchmark-table">
                            <caption className="visually-hidden">Commodity prices day-on-day change</caption>
                            <thead>
                                <tr><th scope="col">Commodity</th><th scope="col">Unit</th><th scope="col">DoD Chg</th></tr>
                            </thead>
                            <tbody>
                                {commodities.rows.map((row, i) => (
                                    <tr key={i}>
                                        <td>{row.name}</td>
                                        <td style={{ color: '#6b7280' }}>{row.unit}</td>
                                        <td><ChangeCell value={row.wowPercent} direction={row.direction} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {commodities.narrative && (
                            <div className="wrap-description">{commodities.narrative}</div>
                        )}
                    </div>
                )}

                {/* IPO Tracker — today + upcoming */}
                {ipoTracker && (
                    (ipoTracker.openingToday?.length > 0 ||
                     ipoTracker.closingToday?.length > 0 ||
                     ipoTracker.listingToday?.length > 0 ||
                     ipoTracker.upcoming?.length > 0 ||
                     ipoTracker.narrative) && (
                        <div className="wrap-section">
                            <h3 className="wrap-section-title">IPO Tracker</h3>

                            {ipoTracker.openingToday?.length > 0 && (
                                <>
                                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.35rem' }}>OPENING TODAY</p>
                                    {ipoTracker.openingToday.map((ipo, i) => (
                                        <div key={i} className="sector-row">
                                            <span>{ipo.name}{ipo.category && <span style={{ color: '#6b7280' }}> ({ipo.category})</span>}</span>
                                            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{ipo.priceBand || ipo.status}</span>
                                        </div>
                                    ))}
                                </>
                            )}

                            {ipoTracker.closingToday?.length > 0 && (
                                <>
                                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.6rem 0 0.35rem' }}>CLOSING TODAY</p>
                                    {ipoTracker.closingToday.map((ipo, i) => (
                                        <div key={i} className="sector-row">
                                            <span>{ipo.name}{ipo.category && <span style={{ color: '#6b7280' }}> ({ipo.category})</span>}</span>
                                            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{ipo.priceBand || ipo.status}</span>
                                        </div>
                                    ))}
                                </>
                            )}

                            {ipoTracker.listingToday?.length > 0 && (
                                <>
                                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.6rem 0 0.35rem' }}>LISTING TODAY</p>
                                    {ipoTracker.listingToday.map((ipo, i) => (
                                        <div key={i} className="sector-row">
                                            <span>{ipo.name}{ipo.category && <span style={{ color: '#6b7280' }}> ({ipo.category})</span>}</span>
                                            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{ipo.status}</span>
                                        </div>
                                    ))}
                                </>
                            )}

                            {ipoTracker.upcoming?.length > 0 && (
                                <>
                                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.6rem 0 0.35rem' }}>UPCOMING</p>
                                    {ipoTracker.upcoming.map((ipo, i) => (
                                        <div key={i} className="sector-row">
                                            <span>{ipo.name}{ipo.category && <span style={{ color: '#6b7280' }}> ({ipo.category})</span>}</span>
                                            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{ipo.priceBand || ipo.status}</span>
                                        </div>
                                    ))}
                                </>
                            )}

                            {ipoTracker.narrative && (
                                <div className="wrap-description" style={{ marginTop: '0.5rem' }}>{ipoTracker.narrative}</div>
                            )}
                        </div>
                    )
                )}

                {/* FII/DII Flows */}
                        {(institutionalFlows || giftNifty?.fii || giftNifty?.dii) && (
                            <div className="wrap-section" style={{ marginTop: '1rem' }}>
                                <h3 className="wrap-section-title">Institutional Flows</h3>

                                {/* Dark FII/DII cash segment cards (moved from GIFT Nifty section) */}
                                {/* {(giftNifty?.fii || giftNifty?.dii) && (
                                    <div className="gift-nifty-cards" style={{ marginBottom: '0.75rem' }}>
                                        {giftNifty.fii && (
                                            <div className="gift-nifty-card fii-card">
                                                <strong>FII's</strong> were {giftNifty.fii.direction === 'Net Sellers' ? 'net sellers' : 'net buyers'} in the cash segment,
                                                amounting to Rs.{Math.abs(giftNifty.fii.amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}Cr on {giftNifty.fii.date}
                                            </div>
                                        )}
                                        {giftNifty.dii && (
                                            <div className="gift-nifty-card dii-card">
                                                <strong>DII's</strong> were {giftNifty.dii.direction === 'Net Sellers' ? 'net sellers' : 'net buyers'} in the cash segment,
                                                amounting to Rs.{Math.abs(giftNifty.dii.amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}Cr on {giftNifty.dii.date}
                                            </div>
                                        )}
                                    </div>
                                )} */}

                                {/* FII/DII net flows from institutionalFlows */}
                                {institutionalFlows && (
                                    <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                                        {institutionalFlows.fiiNet !== undefined && (
                                            <p style={{ margin: '0.2rem 0' }}>
                                                FII: <strong className={institutionalFlows.fiiNet >= 0 ? 'chg-up' : 'chg-down'}>
                                                    ₹{Math.abs(institutionalFlows.fiiNet).toLocaleString('en-IN')}Cr {institutionalFlows.fiiDirection}
                                                </strong>
                                            </p>
                                        )}
                                        {institutionalFlows.diiNet !== undefined && (
                                            <p style={{ margin: '0.2rem 0' }}>
                                                DII: <strong className={institutionalFlows.diiNet >= 0 ? 'chg-up' : 'chg-down'}>
                                                    ₹{Math.abs(institutionalFlows.diiNet).toLocaleString('en-IN')}Cr {institutionalFlows.diiDirection}
                                                </strong>
                                            </p>
                                        )}
                                        {institutionalFlows.note && (
                                            <div className="wrap-description" style={{ marginTop: '0.5rem' }}>
                                                {institutionalFlows.note}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Options snapshot */}
                        {optionsSnapshot && (
                            <div className="wrap-section">
                                <h3 className="wrap-section-title">Options Snapshot</h3>
                                <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                                    <p style={{ margin: '0.2rem 0' }}>
                                        Nifty PCR: <strong>{optionsSnapshot.niftyPCR}</strong>
                                        &nbsp;| Support: <strong>{formatClose(optionsSnapshot.niftySupport)}</strong>
                                        &nbsp;| Resistance: <strong>{formatClose(optionsSnapshot.niftyResistance)}</strong>
                                    </p>
                                    {optionsSnapshot.note && (
                                        <div className="wrap-description" style={{ marginTop: '0.5rem' }}>
                                            {optionsSnapshot.note}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                {/* Events today */}
                {eventsToday?.length > 0 && (
                    <div className="wrap-section">
                        <h3 className="wrap-section-title">Events / Triggers Today</h3>
                        <ul className="events-list">
                            {eventsToday.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                    </div>
                )}

                {/* Our View — always visible */}
                {ourView && (
                    <div className="wrap-section" style={{ marginTop: '1rem' }}>
                        <h3 className="wrap-section-title">Our View</h3>
                        <div className="our-view-box">{ourView}</div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DailyRoundupCard;
